import { Check, CheckCheck, AlertTriangle, FileText, Megaphone } from "lucide-react";
import { getFileUrl } from "@/lib/api";
import { CONVERSATIONS } from "@/constants/testIds";

// Fix M (2026-09-30): contenido de cada burbuja del chat. Archivos (foto,
// audio, video, documento, sticker), links clickeables y estado de entrega.

const URL_RE = /(https?:\/\/[^\s]+)/g;

export function LinkifiedText({ text }) {
  if (!text) return null;
  return text.split(URL_RE).map((part, i) => (
    i % 2 === 1
      ? <a key={i} href={part} target="_blank" rel="noreferrer" className="underline break-all">{part}</a>
      : <span key={i}>{part}</span>
  ));
}

export function MessageMedia({ media }) {
  if (!media) return null;
  if (!media.url) {
    return <p className="text-xs italic opacity-80 mb-1">{media.error || "Archivo no disponible"}</p>;
  }
  const src = getFileUrl(media.url);
  if (media.type === "image" || media.type === "sticker") {
    return (
      <a href={src} target="_blank" rel="noreferrer" className="block mb-1">
        <img
          src={src}
          alt={media.caption || "Imagen"}
          loading="lazy"
          className={media.type === "sticker" ? "w-28 h-28 object-contain" : "max-h-72 max-w-full rounded-lg"}
        />
      </a>
    );
  }
  if (media.type === "audio") {
    // Fix N (2026-09-30): se escucha la copia MP3 si existe (algunos Safari no reproducen OGG).
    return <audio controls preload="none" src={getFileUrl(media.play_url || media.url)} className="w-64 max-w-full mb-1" />;
  }
  if (media.type === "video") {
    return <video controls preload="metadata" src={src} className="max-h-72 max-w-full rounded-lg mb-1" />;
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" className="flex items-center gap-2 underline text-sm mb-1 break-all">
      <FileText className="w-4 h-4 shrink-0" /> {media.filename || "Documento"}
    </a>
  );
}

// Fix AB (2026-10-15): el anuncio desde el que escribio el cliente, arriba de
// su mensaje (como lo muestra WhatsApp en el celular).
export function AdReferral({ referral }) {
  if (!referral) return null;
  const image = referral.image ? getFileUrl(referral.image) : referral.image_url || referral.thumbnail_url;
  const label = referral.source_type === "post" ? "Publicación" : "Anuncio";
  return (
    <div className="mb-2 rounded-lg bg-white/70 border border-border overflow-hidden" data-testid={CONVERSATIONS.adReferral}>
      <div className="flex gap-2 p-2">
        {image && (
          <img src={image} alt="" loading="lazy" className="w-14 h-14 rounded object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Megaphone className="w-3 h-3" /> {label}</p>
          {referral.headline && <p className="text-xs font-medium leading-snug line-clamp-2">{referral.headline}</p>}
          {referral.body && <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">{referral.body}</p>}
          {referral.source_url && (
            <a href={referral.source_url} target="_blank" rel="noreferrer" className="text-[11px] underline">Ver {label.toLowerCase()}</a>
          )}
        </div>
      </div>
    </div>
  );
}

const DELIVERY = {
  sent: { Icon: Check, label: "Enviado" },
  delivered: { Icon: CheckCheck, label: "Entregado" },
  read: { Icon: CheckCheck, label: "Leido" },
  failed: { Icon: AlertTriangle, label: "No se pudo entregar" },
};

export function DeliveryStatus({ status }) {
  const item = DELIVERY[status];
  if (!item) return null;
  const { Icon, label } = item;
  return (
    <span title={label} className="inline-block ml-1 align-[-2px]">
      <Icon className={`w-3 h-3 ${status === "read" ? "text-sky-300" : ""}`} />
    </span>
  );
}

const ERROR_HINTS = {
  131047: "Pasaron mas de 24 horas desde el ultimo mensaje del cliente: hay que escribirle con una plantilla.",
  131026: "El numero no puede recibir el mensaje (puede no tener WhatsApp).",
  131051: "WhatsApp no admite este tipo de mensaje.",
};

export function deliveryErrorText(error) {
  if (!error) return "No se pudo entregar el mensaje.";
  return ERROR_HINTS[error.code] || `No se pudo entregar el mensaje${error.title ? `: ${error.title}` : "."}`;
}
